import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import TablePagination from '@mui/material/TablePagination';
import orderBy from 'lodash/fp/orderBy';
import partition from 'lodash/fp/partition';
import concat from 'lodash/fp/concat';
import filter from 'lodash/fp/filter';
import slice from 'lodash/fp/slice';

import { SingleLineText, Title, SingleLineTextProps } from '@bloomays-lib/ui.shared';
import type { ApolloGqlJobFunnel } from '@bloomays-lib/types.shared';
import { StatusType, TalentBoardEventType } from '@bloomays-lib/types.shared';
import { unaccent } from '@bloomays-lib/utils.shared';

import JobCard from '../molecules/JobCard';
import JobListHeader from '../organisms/JobListHeader';
import { useJobList } from '../../hooks/useJobList';
import { Logger } from '../../services/service-logger';

const logger = Logger('JobList');

const PAGINATION_ROWS_PER_PAGE_OPTIONS = [12, 24, 36, 48];

const JobList = (): React.ReactNode => {
  const { t } = useTranslation(['talentBoard']);
  const { jobs, loading, sortAscending, searchQuery, statusFilter, tamFilter } = useJobList();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PAGINATION_ROWS_PER_PAGE_OPTIONS[0]);

  const sortedJobs = useMemo(() => {
    if (!jobs) return null;

    let filteredJobs = jobs;
    if (tamFilter) {
      filteredJobs = filter((job: ApolloGqlJobFunnel) => job.accountManager?.email === tamFilter)(filteredJobs);
    }

    // Filtrage par nom de job
    if (searchQuery) {
      filteredJobs = filter((job: ApolloGqlJobFunnel) =>
        unaccent(job.name?.toLowerCase() || '').includes(unaccent(searchQuery.toLowerCase())),
      )(filteredJobs);
    }

    // Filtrage par statut
    if (statusFilter !== 'all') {
      filteredJobs = filter((job: ApolloGqlJobFunnel) =>
        statusFilter === StatusType.inProgress ? !job.status : job.status === statusFilter,
      )(filteredJobs);
    }

    const [jobsWithDate, jobsWithoutDate] = partition(
      (job: ApolloGqlJobFunnel) => Boolean(job.expectedStartDate),
      filteredJobs,
    );

    const sorted = orderBy(
      [(job: ApolloGqlJobFunnel) => new Date(job.expectedStartDate as string).getTime()],
      [sortAscending ? 'asc' : 'desc'],
    )(jobsWithDate);

    return concat(sorted, jobsWithoutDate);
  }, [jobs, sortAscending, searchQuery, statusFilter, tamFilter]);

  // Réinitialiser la page quand les filtres changent
  useEffect(() => {
    setPage(0);
  }, [searchQuery, statusFilter, tamFilter, sortAscending]);

  // Calculer les jobs paginés
  const paginatedJobs = useMemo(() => {
    if (!sortedJobs) return null;
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return slice(startIndex, endIndex)(sortedJobs);
  }, [sortedJobs, page, rowsPerPage]);

  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    logger.info('Traffic', {
      page: 'JobList',
      data: { page: newPage },
      evt: { name: TalentBoardEventType.TalentboardJoblistPaginationChangePage },
    });
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    logger.info('Traffic', {
      page: 'JobList',
      data: { rowsPerPage: event.target.value },
      evt: { name: TalentBoardEventType.TalentboardJoblistPaginationChangeRowsPerPage },
    });
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <PageContainer>
      <ContentContainer>
        <Title textTitle={t('homeTitle')} />
        <DescriptionText text={t('jobListMessage')} variant="body1Regular" />
        <JobListHeader />
        {loading ? (
          <Grid container spacing={2} width="100%">
            {[{ id: 1 }, { id: 2 }, { id: 3 }].map((job) => (
              <Grid key={job.id} size={{ xs: 12, sm: 6, md: 6, lg: 4, xl: 3 }}>
                <JobCard loading={true} data={null} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <>
            <Grid container spacing={2} width="100%">
              {paginatedJobs?.map((job) => (
                <Grid key={job.id} size={{ xs: 12, sm: 6, md: 6, lg: 4, xl: 3 }}>
                  <JobCard data={job} loading={false} />
                </Grid>
              ))}
            </Grid>
            {sortedJobs && sortedJobs.length > 0 && (
              <PaginationContainer>
                <TablePagination
                  component="div"
                  count={sortedJobs.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={PAGINATION_ROWS_PER_PAGE_OPTIONS}
                  labelRowsPerPage={t('rowsPerPage')}
                />
              </PaginationContainer>
            )}
          </>
        )}
      </ContentContainer>
    </PageContainer>
  );
};

export default JobList;
export { JobList };

// Styled Components

const PageContainer = styled('div')(({ theme }) => ({
  minHeight: 'calc(100vh - 80px)', // 100vh moins la hauteur du Header (80px)
  flex: 1,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.red[20],
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),

  [theme.breakpoints.down('md')]: {
    justifyContent: 'flex-start',
    paddingTop: theme.spacing(4),
  },
}));

const ContentContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'center',
  gap: theme.spacing(4),
  width: '80.8%',
  margin: '0 auto',

  [theme.breakpoints.down('md')]: {
    alignItems: 'center',
    width: '95%',
    gap: theme.spacing(3),
  },

  [theme.breakpoints.down('sm')]: {
    width: '98%',
    gap: theme.spacing(2),
  },
}));

const DescriptionText: React.FC<SingleLineTextProps> = styled(SingleLineText)(({ theme }) => ({
  textAlign: 'center',
  color: theme.palette.neutral.black,
  lineHeight: 1.6,
  fontSize: '14px',

  // Responsive pour mobile
  [theme.breakpoints.down('md')]: {
    fontSize: '13px',
    textAlign: 'center',
    padding: '0 10px',
  },

  [theme.breakpoints.down('sm')]: {
    fontSize: '12px',
    textAlign: 'center',
    padding: '0 10px',
  },
}));

const PaginationContainer = styled('div')(({ theme }) => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: theme.spacing(2),
}));
